import CardHeader from "components/CardHeader";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import { useDashboardData } from "./DashboardData";
import { pendingHourGroups } from "./dashboardOperationalData";

const PendingHoursCard: React.FC = () => {
    const { intlLocale, t } = useOfficeTranslation();
    const { accounts, accountsFailed, accountsLoading, projects, projectsFailed, projectsLoading } = useDashboardData();
    const groups = useMemo(() => pendingHourGroups(projects, accounts), [accounts, projects]);
    const loading = accountsLoading || projectsLoading;
    const failed = accountsFailed || projectsFailed;
    const count = groups.reduce((total, group) => total + group.entryCount, 0);
    const formatDate = (date: Date) => new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium" }).format(date);
    const formatHours = (minutes: number) => t("accounts.accounts.hoursShort", {
        hours: new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 2 }).format(minutes / 60),
    });

    return <Card className="h-100 mb-0 dashboard-widget-card">
        <CardBody>
            <CardHeader
                title={t("dashboard.pendingHoursCard.title")}
                icon="mdi-timer-sand"
                count={count}
                actions={<Link to="/projects" className="btn btn-sm btn-light" aria-label={t("dashboard.pendingHoursCard.openProjects")}>
                    <i className="mdi mdi-arrow-right" aria-hidden="true" />
                </Link>}
            />
            {loading && <p className="text-muted mt-3 mb-0">{t("dashboard.pendingHoursCard.loading")}</p>}
            {failed && <p className="text-danger mt-3 mb-0">{t("dashboard.pendingHoursCard.loadFailed")}</p>}
            {!loading && !failed && groups.length === 0 && <p className="text-muted mt-3 mb-0">{t("dashboard.pendingHoursCard.empty")}</p>}
            {!loading && !failed && <div className="dashboard-widget-scroll dashboard-operational-list">
                {groups.map(group => <Link
                    className="dashboard-operational-row text-body text-decoration-none"
                    key={`${group.projectId}-${group.workerAccountId}`}
                    to={`/projects/${group.projectId}`}
                >
                    <span className="min-w-0">
                        <strong className="d-block text-truncate">{group.projectName}</strong>
                        <small className="text-muted d-block text-truncate">{group.workerName}</small>
                        <small className="text-muted d-block">{t("dashboard.pendingHoursCard.oldest", { date: formatDate(group.oldestDate) })}</small>
                    </span>
                    <span className="text-end flex-shrink-0">
                        <strong className="d-block">{formatHours(group.minutes)}</strong>
                        <small className="text-muted">{t("dashboard.pendingHoursCard.entries", { count: group.entryCount })}</small>
                    </span>
                </Link>)}
            </div>}
        </CardBody>
    </Card>;
};

export default PendingHoursCard;
